import { readToken } from './auth';
import { supabase } from './supabase';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
  }
}

/**
 * Fetch against the admin API with the bearer token attached.
 *
 * A 401/403 means the 12h token has expired or was rejected, so the session is
 * cleared and the app is sent back to the login screen. The Angular admin had
 * no such handling: an expired token produced a screen of failed requests and
 * empty tables with no explanation, and the only cure was a manual reload.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Always the current (auto-refreshed, if needed) token — Supabase's client
  // refreshes ahead of expiry, so this never hands the API a token that's
  // about to lapse mid-request the way a cached copy might.
  const token = await readToken();

  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (res.status === 401 || res.status === 403) {
    await supabase.auth.signOut();
    // A full reload is the simplest correct reset: it drops every cached query
    // for the old session rather than leaving stale admin data on screen.
    window.location.assign('/login');
    throw new ApiError(res.status, 'unauthorized');
  }

  if (!res.ok) {
    // Nest's exception filters return { message } — prefer that (e.g. the
    // Hungarian "already in progress" text from starting a second newsletter
    // send) over a generic "POST /x failed (409)" the user can't act on.
    const body = await res.clone().json().catch(() => null);
    throw new ApiError(res.status, body?.message ?? `${init.method ?? 'GET'} ${path} failed (${res.status})`);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
